import type Konva from 'konva';
import { useEffect, useRef } from 'react';
import { Rect, Transformer } from 'react-konva';
import { MIN_CROP_SIZE } from '~/features/grid/constants';
import { clamp } from '~/lib/utils';
import { useStore } from '~/store';
import { CROP_COLOR } from './colors';
import { setStageCursor } from './cursors';

export function CropOverlay() {
  const cropRect = useStore((s) => s.grid.cropRect);
  const setCropRect = useStore((s) => s.setCropRect);
  const dims = useStore((s) => s.source.dimensions);
  const drawMode = useStore((s) => s.editor.drawMode);

  const rectRef = useRef<Konva.Rect>(null);
  const trRef = useRef<Konva.Transformer>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: cropRect triggers re-attachment of the transformer after each crop change
  useEffect(() => {
    if (!rectRef.current || !trRef.current) return;
    trRef.current.nodes([rectRef.current]);
    trRef.current.getLayer()?.batchDraw();
  }, [cropRect]);

  if (!cropRect || !dims) return null;

  const interactive = drawMode === 'none' || drawMode === 'crop';

  return (
    <>
      <Rect
        ref={rectRef}
        x={cropRect.x}
        y={cropRect.y}
        width={cropRect.w}
        height={cropRect.h}
        stroke={CROP_COLOR}
        strokeWidth={2}
        strokeScaleEnabled={false}
        fill="rgba(31,166,122,0.05)"
        draggable={interactive}
        listening={interactive}
        onMouseEnter={(e) => setStageCursor(e, 'move')}
        onMouseLeave={(e) => setStageCursor(e, '')}
        onDragStart={() => useStore.temporal.getState().pause()}
        onDragMove={(e) => {
          const node = e.target;
          const x = clamp(0, dims.w - cropRect.w, node.x());
          const y = clamp(0, dims.h - cropRect.h, node.y());
          node.x(x);
          node.y(y);
          setCropRect({
            x: Math.round(x),
            y: Math.round(y),
            w: cropRect.w,
            h: cropRect.h,
          });
        }}
        onDragEnd={() => useStore.temporal.getState().resume()}
        onTransformStart={() => useStore.temporal.getState().pause()}
        onTransform={(e) => {
          const node = e.target as Konva.Rect;
          // Bake scale into width/height so the next frame doesn't multiply again.
          const w = Math.max(1, Math.round(node.width() * node.scaleX()));
          const h = Math.max(1, Math.round(node.height() * node.scaleY()));
          node.width(w);
          node.height(h);
          node.scaleX(1);
          node.scaleY(1);
          setCropRect({ x: Math.round(node.x()), y: Math.round(node.y()), w, h });
        }}
        onTransformEnd={() => useStore.temporal.getState().resume()}
      />
      <Transformer
        ref={trRef}
        visible={interactive}
        listening={interactive}
        rotateEnabled={false}
        ignoreStroke
        anchorSize={10}
        anchorStroke={CROP_COLOR}
        borderStroke={CROP_COLOR}
        enabledAnchors={[
          'top-left',
          'top-right',
          'bottom-left',
          'bottom-right',
          'top-center',
          'bottom-center',
          'middle-left',
          'middle-right',
        ]}
        boundBoxFunc={(oldBox, newBox) => {
          if (newBox.width < MIN_CROP_SIZE || newBox.height < MIN_CROP_SIZE) return oldBox;
          if (newBox.x < 0 || newBox.y < 0) return oldBox;
          if (newBox.x + newBox.width > dims.w + 0.5) return oldBox;
          if (newBox.y + newBox.height > dims.h + 0.5) return oldBox;
          return newBox;
        }}
      />
    </>
  );
}
